import { BaseEhrPatient, BaseItem, NullablePartial } from '../common';

export enum AuthorizationStatus {
  New = 'NEW',
  ProcessingSubmission = 'PROCESSING_SUBMISSION',
  ProcessedApproved = 'PROCESSED_APPROVED',
  ProcessedException = 'PROCESSED_EXCEPTION',
  ProcessedPending = 'PROCESSED_PENDING',
  ProcessedRejected = 'PROCESSED_REJECTED',
  Approved = 'APPROVED',
  Exception = 'EXCEPTION',
  Pending = 'PENDING',
  Rejected = 'REJECTED',
  NotRequired = 'NOT_REQUIRED',
  NewPending = 'NEW_PENDING',
  ProcessingStatusing = 'PROCESSING_STATUSING',
  StatusingPending = 'STATUSING_PENDING',
}

export type RawPatientOrder = NullablePartial<
  Omit<
    BasePatientOrder,
    | 'ehrInformationId'
    | 'referredByEhrProviderId'
    | 'referredToEhrProviderId'
    | 'alternateReferredToEhrProviderId'
    | 'payorAuthorizationNumber'
    | 'deletedTimestamp'
    | 'procedures'
  > & {
    // patient
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    phoneNumber?: string | null;
    email?: string | null;
    patientZipcode?: string | null;
    // encounter
    encounterEhrId?: string;
    // patient insurance information
    planName?: string;
    secondaryPlanName?: string;
    memberId?: string;
    secondaryMemberId?: string;
    groupNumber?: string;
    secondaryGroupNumber?: string;
    payor?: string;
    secondaryPayor?: string;
    // ehr (patient) information
    patientMRN: string;
    // ehr referred by provider information
    referredByProviderEhrId: string;
    referredByProviderPhoneNumber: string | null;
    referredByProviderFirstName: string | null;
    referredByProviderLastName: string | null;
    referredByProviderNpi: string | null;
    // ehr referred to provider information
    referredToProviderEhrId: string | null;
    referredToProviderPhoneNumber: string | null;
    referredToProviderFirstName: string | null;
    referredToProviderLastName: string | null;
    referredToProviderNpi: string | null;
    alternateReferredToProviderEhrId: string | null;
    // auth status
    authorizationStatus?: string;
    // return to clinic order
    followUpTime?: string;
    // diagnosis
    diagnoses?: string;
    // reason for exam
    reasonForExam?: string | null;
    // noa
    patientHospitalLocation?: string | null;
    patientType?: string | null;
  }
>;

export interface ImportPatientOrder extends RawPatientOrder {}

export const IMPORT_COLUMNS: (keyof ImportPatientOrder)[] = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'phoneNumber',
  'email',
  'encounterEhrId',
  'planName',
  'secondaryPlanName',
  'memberId',
  'secondaryMemberId',
  'groupNumber',
  'secondaryGroupNumber',
  'payor',
  'secondaryPayor',
  'patientMRN',
  'ehrCreated',
  'ehrScheduled',
  'referredByProviderEhrId',
  'referredByProviderPhoneNumber',
  'referredByProviderFirstName',
  'referredByProviderLastName',
  'referredByProviderNpi',
  'referredToProviderEhrId',
  'referredToProviderPhoneNumber',
  'referredToProviderFirstName',
  'referredToProviderLastName',
  'referredToProviderNpi',
  'alternateReferredToProviderEhrId',
  'ehrReferenceValue',
  'referredByDepartment',
  'referredToDepartment',
  'rawProcedures',
  'rawDiagnoses',
  'payerAuthorizationNumber',
  'registrationTime',
  'dischargeTime',
  'authorizationStatus',
  'specialty',
  'followUpTime',
  'followUpVisitType',
  'fin',
  'trackingNumber',
  'seriesAppointmentCount',
  'diagnoses',
  'reasonForExam',
  'patientZipcode',
  'patientHospitalLocation',
  'patientType',
];

interface BasePatientOrder {
  ehrInformationId: string;
  referredByEhrProviderId: string;
  referredToEhrProviderId: string | null;
  ehrCreated: Date | null;
  ehrScheduled: Date | null;
  specialty: string | null;
  ehrReferenceValue: string | null;
  referredByDepartment: string | null;
  referredToDepartment: string | null;
  rawProcedures: string | null;
  rawDiagnoses: string | null;
  payerAuthorizationNumber: string | null;
  registrationTime: Date | null;
  dischargeTime: Date | null;
  deletedTimestamp: Date | null;
  followUpVisitType: string | null;
  followUpValue: number | null;
  followUpUnit: string | null;
  ehrTimeSlotBookingId: number | null;
  payer: string | null;
  fin: string | null;
  alternateReferredToEhrProviderId: string | null;
  reasonForExam: string | null;
  trackingNumber: string | null;
  authEffectiveDate: Date | null;
  authExpirationDate: Date | null;
  seriesAppointmentCount: number | null;
  patientZipcode: string | null;
  isTest: boolean | null;
  patientHospitalLocation: string | null;
  patientType: string | null;
}

export interface PatientOrder extends BaseItem, BasePatientOrder {}

export type CreateValues = Partial<
  Omit<BasePatientOrder, 'created' | 'updated'>
>;
export type UpdateValues = Partial<
  Omit<BasePatientOrder, 'created' | 'updated'>
>;

export interface Diagnosis {
  description: string;
  icd10Codes: string[] | null;
}
export interface Procedure {
  description: string;
  cptCode: string | null;
}
export interface PatientOrderPopulated extends PatientOrder, BaseEhrPatient {
  npi: string | null;
  authorizationStatus: AuthorizationStatus | null;
  authorizationNote: string | null;
  authorizationNotRequiredReason: NotRequiredReason | null;
  authorizationNotRequiredComment: string | null;
  diagnoses: Diagnosis[];
  procedures: Procedure[];
}

export enum RejectedReason {
  EligibilityIssue = 'Eligibility Issue',
  NoDiagnosis = 'No Diagnosis',
  NoProcedure = 'No Procedure',
  LackOfDocumentation = 'Lack of documentation',
  PayerPortalIssues = 'Payer portal issues',
  PeerToPeerReview = 'Peer to Peer review',
  IncompleteDocumentation = 'Incomplete documentation',
  Scheduled1DayBeforeProcedure = 'Scheduled 1 day before procedure',
  Scheduled2DaysBeforeProcedure = 'Scheduled 2 days before procedure',
  TimeWindowTooSmall = 'Time window too small',
  CancelledAppointments = 'Cancelled appointments',
  RescheduledAppointments = 'Rescheduled appoints',
  OutOfNetwork = 'Out of network',
  DuplicateRequest = 'Duplicate request',
  Other = 'Other',
}

export enum NotRequiredReason {
  NoAuthRequired = 'Procedure does not require authorization',
  OutsideSystem = 'Procedure outside of healthcare system',
  ExistingAuth = 'Existing authorization obtained',
  Other = 'Other',
}

export type UpdateStatus =
  | {
      status: AuthorizationStatus.Pending;
      payerAuthorizationNumber: string | null;
      authorizationNote?: string;
    }
  | {
      status: AuthorizationStatus.Rejected;
      rejectedReason: RejectedReason;
      rejectedComment?: string;
      authorizationNote?: string;
    }
  | {
      status: AuthorizationStatus.NotRequired;
      notRequiredReason: NotRequiredReason;
      notRequiredComment?: string;
      authorizationNote?: string;
    }
  | {
      status: AuthorizationStatus.Approved;
      payerAuthorizationNumber: string;
    };
